/* =========================================================================
 *  FM Tile Kit builder — Fatigue-Step interlocking tile system
 *  ----------------------------------------------------------
 *  Turns "how big is your floor?" into a priced bill of materials and one
 *  multi-line add-to-cart. Replaces the variant dropdown on
 *  `templates/product.tile-kit.json`.
 *
 *  Reads the parts model emitted by `snippets/fm-tile-kit.liquid`. Prices are
 *  the resolved variants' own `variant.price` in pence — nothing here knows a
 *  price, and the mock's placeholder figures deliberately did not survive.
 *
 *  One component, three surfaces
 *    desktop  steps + kit + total + CTA in the buy column; the SVG plan takes
 *             the gallery's main-image slot.
 *    sheet    the same component inside the mobile bottom sheet, with the
 *             plan inline below step 3 and the total + CTA pinned.
 *    buybar   the mobile closed-state footer: slim before a kit exists,
 *             summary + Add to basket once it does.
 *    `renderControls()` builds all of them from one code path — the round-2
 *    options-sheet lesson was that two render paths for the same decision
 *    drift within a week.
 *
 *  Reusability
 *    Nothing below names Fatigue-Step. The geometry is "square base unit +
 *    handed perimeter strips", driven entirely by `unitSize`, `units` and
 *    `perimeter` in the data island, so a second product of the same shape
 *    needs a new island and no new JS.
 * ========================================================================= */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 767;

  /* Guard against float noise in the ceil: 2.7 / 0.9 is 3.0000000000000004 in
     IEEE754, which would ask for a fourth row of tiles on an exact fit. */
  var CEIL_EPSILON = 1e-9;

  /* Above this many tiles per side the plan stops drawing individual tiles and
     becomes an outline with a count. Past ~15 the cells are too small to read
     and the SVG node count stops being free. */
  var SCHEMATIC_AFTER = 15;

  var SIDES = ['top', 'right', 'bottom', 'left'];

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  /* Thousands-separated, to match Shopify's `money` filter. A kit runs to
     four figures routinely, and "£1114.70" next to the page's own
     "£1,114.70" reads as a different pricing engine. */
  function money(pence) {
    var v = (pence / 100).toFixed(2);
    var parts = v.split('.');
    return '£' + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
  }

  function round2(n) {
    return Math.round(n * 100) / 100;
  }

  /* Page VAT mode. fm-vat.js owns it (the Google Shopping `?incl_vat=true`
     rule, gclid, the manual toggle); the URL test is only the fallback for the
     moment before that script has run. */
  function vatInc() {
    if (window.fmVatMode) return window.fmVatMode === 'inc';
    return /[?&]incl_vat=true/i.test(window.location.search);
  }

  /* DISPLAY money — follows the page's VAT mode. The CTA deliberately does
     not: sitewide rule is that headline prices flip and the cart button
     stays ex VAT. */
  function dispMoney(pence) {
    return money(vatInc() ? Math.round(pence * 1.2) : pence);
  }

  function vatSuffix() {
    return vatInc() ? 'Inc. VAT' : '+ VAT';
  }

  /* ------------------------------------------------------------------ *
   *  Tiny DOM builders
   * ------------------------------------------------------------------ */
  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v === null || v === undefined || v === false) return;
        if (k === 'text') { n.textContent = v; return; }
        if (k === 'html') { n.innerHTML = v; return; }
        if (k === 'on') {
          Object.keys(v).forEach(function (ev) { n.addEventListener(ev, v[ev]); });
          return;
        }
        if (v === true) { n.setAttribute(k, ''); return; }
        n.setAttribute(k, v);
      });
    }
    append(n, kids);
    return n;
  }

  var SVG_NS = 'http://www.w3.org/2000/svg';

  function svg(tag, attrs, kids) {
    var n = document.createElementNS(SVG_NS, tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v === null || v === undefined || v === false) return;
        if (k === 'text') { n.textContent = v; return; }
        if (k === 'on') {
          Object.keys(v).forEach(function (ev) { n.addEventListener(ev, v[ev]); });
          return;
        }
        n.setAttribute(k, v);
      });
    }
    append(n, kids);
    return n;
  }

  function append(parent, kids) {
    if (kids === null || kids === undefined) return;
    if (!Array.isArray(kids)) kids = [kids];
    kids.forEach(function (k) {
      if (k === null || k === undefined || k === false) return;
      parent.appendChild(typeof k === 'string' ? document.createTextNode(k) : k);
    });
  }

  function clear(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  document.addEventListener('DOMContentLoaded', function () {
    var dataNode = document.querySelector('[data-fm-kit-data]');
    if (!dataNode) return;

    var DATA;
    try {
      DATA = JSON.parse(dataNode.textContent);
    } catch (e) {
      /* A malformed island means no parts and therefore no prices. Better an
         absent calculator (the noscript block and the phone number are still
         on the page) than a builder that adds the wrong SKU. */
      return;
    }
    if (!DATA || !DATA.units || !DATA.units.length) return;

    var controlsMount = document.querySelector('[data-fm-kit-controls]');
    var sheet = document.querySelector('[data-fm-kit-sheet]');
    var sheetBody = document.querySelector('[data-fm-kit-sheet-body]');
    var sheetFooter = document.querySelector('[data-fm-kit-sheet-footer]');
    var bar = document.querySelector('[data-fm-kit-buybar]');
    if (!controlsMount) return;

    var UNIT_SIZE = DATA.unitSize || 0.9;
    var MAX_AREA = DATA.maxAreaM2 || 50;
    var PALLET_KG = DATA.palletThresholdKg || 150;

    /* Side → pair mapping. UNCONFIRMED with COBA (handover open item #2):
       swap the two arrays in the data island if male serves bottom + left. */
    var PAIR_OF = {};
    SIDES.forEach(function (s) {
      PAIR_OF[s] = (DATA.sidePairs && DATA.sidePairs.A || []).indexOf(s) > -1 ? 'A' : 'B';
    });

    var unitsById = {};
    DATA.units.forEach(function (u) { unitsById[u.id] = u; });

    /* ---------------------------------------------------------------- *
     *  Bulk tiers
     *  ----------
     *  Measured against /cart.js on the live product, the discount is:
     *    - banded on TOTAL eligible quantity in the cart, not per line
     *      (2 tiles alone → 0%; 2 tiles beside 20 strips → both 9%)
     *    - applied as an integer-pence floor PER UNIT, which is the same
     *      rule `fm-options-sheet.js` (`tierUnit`) already uses
     *
     *  Both matter for matching to the penny. Discounting the line total
     *  instead of the unit is out by pennies at most quantities, and using
     *  the line's own quantity for the band understates every kit — a kit is
     *  26 items across three lines, none of which reaches 20 on its own.
     * ---------------------------------------------------------------- */
    var TIERS = (DATA.tiers || []).slice().sort(function (a, b) { return a[0] - b[0]; });

    /* Quantity of this product ALREADY in the cart. The band counts the whole
       cart, so a kit added on top of existing tiles earns a bigger break than
       its own item count implies. Refreshed on load and after every add;
       until it resolves the kit quotes its own quantity alone, which can only
       ever understate the discount — the safe direction to be wrong in. */
    var cartQty = 0;

    function refreshCartQty() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var n = 0;
          (cart.items || []).forEach(function (i) {
            if (i.product_id === DATA.productId) n += i.quantity;
          });
          if (n !== cartQty) { cartQty = n; render(); }
        })
        .catch(function () { /* quote without it rather than not at all */ });
    }

    /* Highest tier whose threshold the quantity reaches. */
    function tierFor(qty) {
      var pct = 0;
      TIERS.forEach(function (t) { if (qty >= t[0]) pct = t[1]; });
      return pct;
    }

    /* Discounted unit price, floored to whole pence — matches what the cart
       charges exactly. */
    function tierUnit(base, pct) {
      if (!pct) return base;
      return base - Math.floor(base * pct / 100);
    }

    /* ---------------------------------------------------------------- *
     *  State
     * ---------------------------------------------------------------- */
    var state = {
      mode: 'calc',
      unit: 'm',
      w: '',
      l: '',
      type: 'solid',
      colour: 'yellow',
      edges: { top: true, right: true, bottom: true, left: true },
      /* Absolute per-line overrides. Null means "follow the calculation";
         a number (including 0) means the shopper set it by hand. */
      ov: { tile: null, A: null, B: null },
      manual: { solid: 0, drain: 0, edgeA: 0, edgeB: 0 },
      leadVal: '',
      leadSent: false,
      leadBusy: false,
      busy: false,
      added: false,
      error: ''
    };

    function clearOv() {
      return { tile: null, A: null, B: null };
    }

    /* ANY change to steps 1–3 clears overrides (handover §4). Overrides are
       absolute quantities, so a kit recalculated under them would silently
       keep the old numbers against new dimensions. */
    function setStep(patch) {
      Object.keys(patch).forEach(function (k) { state[k] = patch[k]; });
      state.ov = clearOv();
      state.added = false;
      state.error = '';
      /* Push the auto-scroll back while they are still typing. `hasKit` turns
         true on the first keystroke of the second dimension, so without this
         the page would move under someone part-way through "2.65". */
      if (frameTimer) scheduleFrameKit();
      render();
    }

    function tilePart() {
      return unitsById[state.type === 'solid' ? 'solid' : 'drain'] || null;
    }

    function stripPart(pair) {
      var found = null;
      (DATA.perimeter || []).forEach(function (p) {
        if (found) return;
        if (p.pairKey === pair && p.colour === state.colour) found = p;
      });
      return found;
    }

    /* Manual mode addresses strips by pair too, so a colour swap in that mode
       re-points the same two rows rather than adding two more. */
    /* ---------------------------------------------------------------- *
     *  What this product actually stocks
     *  --------------------------------
     *  The same builder serves products that carry only part of the range —
     *  the gym-tile listing is the same tiles marketed differently, with no
     *  drainage tile and no yellow edging. Offering a choice that resolves to
     *  no variant is a dead end: the shopper picks it, every price becomes
     *  "—" and Add to basket refuses with no explanation.
     *
     *  So the options ARE the parts. Anything that did not resolve is simply
     *  not offered, which needs no per-product configuration and keeps
     *  working when the catalogue changes.
     * ---------------------------------------------------------------- */
    function resolved(part) {
      return !!(part && part.variantId);
    }

    var TILE_TYPES = [
      { id: 'solid', label: 'Solid', part: unitsById.solid },
      { id: 'drainage', label: 'Drainage holes', part: unitsById.drain }
    ].filter(function (t) { return resolved(t.part); });

    /* A colour needs BOTH of its handed strips — half a pair cannot edge a
       mat, and quoting one side only would under-order. */
    var EDGE_COLOURS = ['yellow', 'black'].filter(function (col) {
      var a = null, b = null;
      (DATA.perimeter || []).forEach(function (p) {
        if (p.colour !== col) return;
        if (p.pairKey === 'A') a = p;
        if (p.pairKey === 'B') b = p;
      });
      return resolved(a) && resolved(b);
    });

    function manualPart(key) {
      if (key === 'solid') return unitsById.solid || null;
      if (key === 'drain') return unitsById.drain || null;
      return stripPart(key === 'edgeA' ? 'A' : 'B');
    }

    /* ================================================================ *
     *  Calculation — handover §4
     * ================================================================ */
    function calc() {
      var s = state;
      var wN = parseFloat(s.w) || 0;
      var lN = parseFloat(s.l) || 0;

      var tw = 0;
      var tl = 0;
      if (s.unit === 'm') {
        /* Sub-tile entries round UP to one tile, which is the documented
           minimum — a 0.4 m bay still needs a whole tile. */
        if (wN > 0) tw = Math.ceil(wN / UNIT_SIZE - CEIL_EPSILON);
        if (lN > 0) tl = Math.ceil(lN / UNIT_SIZE - CEIL_EPSILON);
      } else {
        tw = Math.max(0, Math.round(wN));
        tl = Math.max(0, Math.round(lN));
      }

      var empty = s.mode === 'calc' && (!tw || !tl);
      var covW = round2(tw * UNIT_SIZE);
      var covL = round2(tl * UNIT_SIZE);
      var covArea = round2(covW * covL);

      /* Range is tested against what the shopper TYPED, not the rounded-up
         coverage — otherwise a 49.8 m² entry that rounds to 50.4 would be
         refused for crossing a limit they never asked to cross. */
      var enteredArea = s.unit === 'm' ? round2(wN * lN) : covArea;

      var e = s.edges;
      var eA = (e.top ? tw : 0) + (e.right ? tl : 0);
      var eB = (e.bottom ? tw : 0) + (e.left ? tl : 0);
      var tiles = tw * tl;
      var over = s.mode === 'calc' && !empty && enteredArea > MAX_AREA;

      var ov = s.ov || clearOv();
      var effT = tiles;
      var effA = eA;
      var effB = eB;
      var rows = [];

      if (s.mode === 'calc' && !empty && !over) {
        effT = ov.tile === null ? tiles : ov.tile;
        effA = ov.A === null ? eA : ov.A;
        effB = ov.B === null ? eB : ov.B;

        rows.push(row('tile', effT, tilePart()));
        /* A pair with no calculated need still shows once it has been
           overridden — otherwise stepping "female" up from zero would make
           the row vanish on the next render. */
        if (eA || ov.A !== null) rows.push(row('A', effA, stripPart('A')));
        if (eB || ov.B !== null) rows.push(row('B', effB, stripPart('B')));
      }

      if (s.mode === 'manual') {
        ['solid', 'drain', 'edgeA', 'edgeB'].forEach(function (k) {
          var q = s.manual[k] || 0;
          if (!q) return;
          var kind = k === 'solid' || k === 'drain' ? 'tile' : (k === 'edgeA' ? 'A' : 'B');
          rows.push(row(kind, q, manualPart(k)));
        });
      }

      /* Tier pass. The band needs the whole kit's quantity, so it can only be
         resolved once every row exists — hence a second pass rather than
         pricing each row as it is built. */
      var items = 0;
      rows.forEach(function (r) { items += r.qty; });

      var bandQty = items + cartQty;
      var tierPct = TIERS.length ? tierFor(bandQty) : 0;

      var totalEx = 0;
      var totalList = 0;
      var priceable = true;
      rows.forEach(function (r) {
        if (r.unitPrice === null) {
          if (r.qty) priceable = false;
          return;
        }
        r.unitList = r.unitPrice;
        r.unitNow = tierUnit(r.unitPrice, tierPct);
        r.price = r.qty * r.unitNow;
        r.listPrice = r.qty * r.unitList;
        if (!r.qty) return;
        totalEx += r.price;
        totalList += r.listPrice;
      });

      var saving = totalList - totalEx;

      var tileCount = 0;
      var stripCount = 0;
      rows.forEach(function (r) {
        if (r.kind === 'tile') tileCount += r.qty;
        else stripCount += r.qty;
      });
      var weight = tileCount * (DATA.tileWeightKg || 0) + stripCount * (DATA.stripWeightKg || 0);

      var adjusted = s.mode === 'calc' && !empty && !over &&
        (effT !== tiles || effA !== eA || effB !== eB);

      return {
        tw: tw, tl: tl, wN: wN, lN: lN,
        covW: covW, covL: covL, covArea: covArea, enteredArea: enteredArea,
        eA: eA, eB: eB, tiles: tiles,
        effT: effT, effA: effA, effB: effB,
        empty: empty, over: over, adjusted: adjusted,
        rows: rows, totalEx: totalEx, items: items, priceable: priceable,
        totalList: totalList, saving: saving, tierPct: tierPct, bandQty: bandQty,
        weight: weight
      };
    }

    /* Prices are filled in by the tier pass in `calc()`, not here — the
       discount band depends on the whole kit's quantity, which is not known
       until every row exists. */
    function row(kind, qty, part) {
      var unit = part && part.price !== null && part.price !== undefined ? part.price : null;
      return {
        kind: kind,
        qty: qty,
        part: part,
        label: part ? part.label : 'Part unavailable',
        sub: part ? part.sub : '',
        /* Null unit price = the part did not resolve to a variant. The line
           still renders (so the gap is visible rather than silently dropped)
           but the kit cannot be added. */
        unitPrice: unit,
        unitList: unit,
        unitNow: unit,
        price: unit === null ? null : qty * unit,
        listPrice: unit === null ? null : qty * unit
      };
    }

    /* ================================================================ *
     *  Layout plan — handover §7
     * ================================================================ */
    var PROFILES = {
      split: { E: 18, ML: 34, MT: 20, W: 560, H: 400, max: 80 },
      std:   { E: 14, ML: 30, MT: 18, W: 430, H: 260, max: 56 },
      mini:  { E: 10, ML: 24, MT: 14, W: 330, H: 178, max: 36 }
    };

    function buildPlan(c, size) {
      var s = state;
      var dims = PROFILES[size] || PROFILES.std;

      /* Empty state draws a ghost 4 × 3 at 35% so the panel is never a blank
         grey box — the shopper can see what they are about to get. */
      var ghost = c.empty || !c.tw || !c.tl;
      var tw = ghost ? 4 : c.tw;
      var tl = ghost ? 3 : c.tl;
      if (!tw || !tl) return null;

      var e = ghost ? { top: true, right: true, bottom: true, left: true } : s.edges;
      var anyEdge = ghost || SIDES.some(function (k) { return e[k]; });
      var schematic = tw > SCHEMATIC_AFTER || tl > SCHEMATIC_AFTER;

      var E = dims.E, ML = dims.ML, MT = dims.MT, MR = 10, MB = 10;
      var availW = dims.W - ML - MR - 2 * E;
      var availH = dims.H - MT - MB - 2 * E;
      var cell = Math.min(dims.max, availW / tw, availH / tl);
      cell = schematic ? Math.max(2, cell) : Math.max(7, Math.floor(cell));

      var gw = tw * cell;
      var gh = tl * cell;
      var x0 = ML + (e.left ? E : 0);
      var y0 = MT + (e.top ? E : 0);
      var W = x0 + gw + (e.right ? E : 0) + MR;
      var H = y0 + gh + (e.bottom ? E : 0) + MB;

      var yellow = s.colour === 'yellow';
      var band = yellow ? '#f2c230' : '#4a4f57';
      var mot = yellow ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.6)';

      var kids = [];

      if (schematic) {
        kids.push(svg('rect', { x: x0, y: y0, width: gw, height: gh, fill: '#262a2e', stroke: '#3a3f45' }));
        kids.push(svg('text', {
          x: x0 + gw / 2, y: y0 + gh / 2 + 4, 'text-anchor': 'middle',
          'font-size': 12, fill: '#cfd3d8', text: tw + ' × ' + tl + ' tiles'
        }));
      } else {
        for (var r = 0; r < tl; r++) {
          for (var col = 0; col < tw; col++) {
            var x = x0 + col * cell;
            var y = y0 + r * cell;
            kids.push(svg('rect', {
              x: x, y: y, width: cell, height: cell,
              fill: '#262a2e', stroke: '#171a1d', 'stroke-width': 1
            }));

            /* The real 300 mm dividing lines — the ones a fitter cuts along.
               Only drawn once a cell is big enough for three of them to read
               as a grid rather than as noise. */
            if (cell >= 24) {
              for (var i = 1; i < 3; i++) {
                kids.push(svg('line', {
                  x1: x + cell * i / 3, y1: y + 1, x2: x + cell * i / 3, y2: y + cell - 1,
                  stroke: 'rgba(255,255,255,0.07)'
                }));
                kids.push(svg('line', {
                  x1: x + 1, y1: y + cell * i / 3, x2: x + cell - 1, y2: y + cell * i / 3,
                  stroke: 'rgba(255,255,255,0.07)'
                }));
              }
            }

            /* Drainage holes are filled with the PANEL background, not with a
               darker grey: they go straight through the tile, so what you see
               is the floor under it. A faint rim keeps them from reading as
               white dots painted on. */
            if (s.type === 'drainage' && cell >= 16) {
              for (var a = 0; a < 3; a++) {
                for (var b = 0; b < 3; b++) {
                  kids.push(svg('circle', {
                    cx: x + cell * (2 * a + 1) / 6,
                    cy: y + cell * (2 * b + 1) / 6,
                    r: Math.max(1.5, cell / 14),
                    fill: '#f6f6f6', stroke: 'rgba(0,0,0,0.28)', 'stroke-width': 0.75
                  }));
                }
              }
            }
          }
        }
      }

      var sides = {
        top:    { x: x0, y: y0 - E, w: gw, h: E, axis: 'h' },
        bottom: { x: x0, y: y0 + gh, w: gw, h: E, axis: 'h' },
        left:   { x: x0 - E, y: y0, w: E, h: gh, axis: 'v' },
        right:  { x: x0 + gw, y: y0, w: E, h: gh, axis: 'v' }
      };

      /* The 14px band is ~2x the true 75mm scale. Deliberate: at true scale
         the edging is two pixels and the tab/slot motifs — the only thing
         distinguishing the two strip types without colour — disappear. */
      SIDES.forEach(function (k) {
        var b = sides[k];
        if (e[k]) {
          kids.push(svg('rect', {
            x: b.x, y: b.y, width: b.w, height: b.h,
            fill: band, stroke: 'rgba(0,0,0,0.25)', 'stroke-width': 1
          }));

          if (!schematic && size !== 'mini') {
            var n = b.axis === 'h' ? tw : tl;
            if (PAIR_OF[k] === 'A') {
              /* Male — square tabs, one per tile edge. */
              for (var i = 0; i < n; i++) {
                if (b.axis === 'h') {
                  kids.push(svg('rect', {
                    x: b.x + i * cell + cell / 2 - 2.5,
                    y: k === 'top' ? b.y + b.h - 5 : b.y,
                    width: 5, height: 5, fill: mot
                  }));
                } else {
                  kids.push(svg('rect', {
                    x: k === 'right' ? b.x : b.x + b.w - 5,
                    y: b.y + i * cell + cell / 2 - 2.5,
                    width: 5, height: 5, fill: mot
                  }));
                }
              }
            } else if (b.axis === 'h') {
              /* Female — a dashed slot line. */
              kids.push(svg('line', {
                x1: b.x + 3, y1: b.y + b.h / 2, x2: b.x + b.w - 3, y2: b.y + b.h / 2,
                stroke: mot, 'stroke-width': 2, 'stroke-dasharray': '6 4'
              }));
            } else {
              kids.push(svg('line', {
                x1: b.x + b.w / 2, y1: b.y + 3, x2: b.x + b.w / 2, y2: b.y + b.h - 3,
                stroke: mot, 'stroke-width': 2, 'stroke-dasharray': '6 4'
              }));
            }
          }
        } else if (!ghost && anyEdge) {
          /* A disabled side keeps a dashed ghost band. Removing it outright
             would leave nothing to aim at when re-enabling the edge. */
          kids.push(svg('rect', {
            x: b.x, y: b.y, width: b.w, height: b.h,
            fill: 'none', stroke: '#b9bdc7', 'stroke-dasharray': '4 4', 'stroke-width': 1
          }));
        }
      });

      /* Corner squares where two enabled sides meet, with a diagonal tick —
         there is no corner SKU; every strip carries a trimmable corner. */
      var corners = [
        ['top', 'left', x0 - E, y0 - E],
        ['top', 'right', x0 + gw, y0 - E],
        ['bottom', 'left', x0 - E, y0 + gh],
        ['bottom', 'right', x0 + gw, y0 + gh]
      ];
      corners.forEach(function (cn) {
        if (!e[cn[0]] || !e[cn[1]]) return;
        kids.push(svg('rect', {
          x: cn[2], y: cn[3], width: E, height: E,
          fill: band, stroke: 'rgba(0,0,0,0.35)', 'stroke-width': 1
        }));
        kids.push(svg('line', {
          x1: cn[2] + 2, y1: cn[3] + 2, x2: cn[2] + E - 2, y2: cn[3] + E - 2,
          stroke: mot, 'stroke-width': 1
        }));
      });

      if (!ghost) {
        kids.push(svg('text', {
          x: x0 + gw / 2, y: 11, 'text-anchor': 'middle', 'font-size': 10,
          fill: '#6b7280', text: c.covW.toFixed(1) + ' m'
        }));
        kids.push(svg('text', {
          x: 11, y: y0 + gh / 2,
          transform: 'rotate(-90 11 ' + (y0 + gh / 2) + ')',
          'text-anchor': 'middle', 'font-size': 10,
          fill: '#6b7280', text: c.covL.toFixed(1) + ' m'
        }));

        /* Oversized invisible hit rects — the visible band is 10–18px, which
           is under every touch-target guideline on its own. */
        SIDES.forEach(function (k) {
          var b = sides[k];
          kids.push(svg('rect', {
            x: b.x - 3, y: b.y - 3, width: b.w + 6, height: b.h + 6,
            fill: 'rgba(0,0,0,0)', class: 'fm-kit-plan__hit',
            on: { click: function () { toggleSide(k); } }
          }, svg('title', { text: (e[k] ? 'Remove' : 'Add') + ' edging on this side' })));
        });
      }

      /* No inline max-width. The mock's panel was a fixed 560 × 400 box, so
         capping the SVG at its own viewBox width was free there — but the
         theme's gallery slot is a ~550px SQUARE, and the cap left a 400px
         plan marooned in it with dead grey on every side. Sizing is CSS's
         job now, per surface. An inline style could not be overridden from
         the stylesheet anyway. */
      return svg('svg', {
        viewBox: '0 0 ' + W + ' ' + H,
        class: 'fm-kit-plan__svg' + (ghost ? ' fm-kit-plan__svg--ghost' : '') +
          ' fm-kit-plan__svg--' + size,
        role: 'img',
        'aria-label': 'Layout plan, ' + tw + ' by ' + tl + ' tiles'
      }, kids);
    }

    function toggleSide(k) {
      var e = {};
      SIDES.forEach(function (s) { e[s] = state.edges[s]; });
      e[k] = !e[k];
      setStep({ edges: e });
    }

    /* Keyboard parity for the plan. The hit rects are pointer-only, so the
       same four toggles are also real buttons — visually hidden, but focusable
       and announced with their pressed state.

       The label stays constant and `aria-pressed` carries the state. Naming
       the action instead ("Remove edging on the top side") double-encodes it:
       a screen reader would announce "remove edging, pressed", which reads as
       though the removal is what is already done. */
    function planKeyboardControls() {
      return el('div', { class: 'fm-kit-plan__keys' }, SIDES.map(function (k) {
        return el('button', {
          type: 'button',
          'aria-pressed': String(state.edges[k]),
          text: 'Edging on the ' + k + ' side',
          on: { click: function () { toggleSide(k); } }
        });
      }));
    }

    function planTextEquivalent(c) {
      if (c.empty) return 'No area entered yet.';
      var on = SIDES.filter(function (k) { return state.edges[k]; });
      var where = on.length === 4 ? 'all four sides'
        : on.length === 0 ? 'no sides'
        : on.join(', ');
      return 'Layout plan: ' + c.tw + ' by ' + c.tl + ' ' +
        (state.type === 'solid' ? 'solid' : 'drainage') + ' tiles, edging on ' + where + '.';
    }

    /* ================================================================ *
     *  Copy — handover §13
     * ================================================================ */
    /* Returns a headline and an optional second line. The rounding case is the
       one shoppers act on — it is the difference between the mat they pictured
       and the mat that turns up — so it gets a heading they can read at a
       glance plus the "you can cut it" reassurance underneath, rather than one
       long amber sentence they skim past (Jack, 07-Aug-2026). */
    function statusFor(c) {
      if (state.mode !== 'calc' || c.empty) return { text: '', detail: '', tone: '' };

      if (c.over) {
        return {
          text: 'That’s ' + c.enteredArea.toFixed(1) + ' m² — over our ' +
            MAX_AREA + ' m² online limit.',
          detail: 'See the project-price panel below.',
          tone: 'warn'
        };
      }

      var isM = state.unit === 'm';
      var size = c.covW.toFixed(1) + ' m × ' + c.covL.toFixed(1) + ' m';

      /* 5mm tolerance: someone typing 3.6 for a 3.6 m fit should not be told
         their kit is "slightly larger" because of float representation. */
      if (isM && (Math.abs(c.covW - c.wN) > 0.005 || Math.abs(c.covL - c.lN) > 0.005)) {
        return {
          text: 'Rounded up to ' + size + ' — a little larger than the ' +
            state.w + ' m × ' + state.l + ' m you entered.',
          detail: 'Kits are built from whole ' + UNIT_SIZE + ' m tiles. ' +
            'If you need an exact fit, tiles cut cleanly along their 300 mm ' +
            'lines and still interlock.',
          tone: 'note'
        };
      }

      return {
        text: (isM ? 'Covers exactly ' : c.tw + ' × ' + c.tl + ' tiles covers ') +
          size + ' (' + c.covArea.toFixed(1) + ' m²).',
        detail: '',
        tone: ''
      };
    }

    function edgeEcho(c) {
      if (c.empty && state.mode === 'calc') return '';
      var on = SIDES.filter(function (k) { return state.edges[k]; });
      if (!on.length) return 'None';
      var word = state.colour === 'yellow' ? 'Yellow' : 'Black';
      if (on.length === 4) return word;
      return word + ', ' + on.length + (on.length === 1 ? ' side' : ' sides');
    }

    function areaHint(c) {
      var isM = state.unit === 'm';
      if (c.empty) {
        return isM
          ? 'Rough is fine — we round up to whole tiles and show you the exact finished size. Most workshop bays are around 3 m × 2 m.'
          : 'Tiles are ' + UNIT_SIZE + ' × ' + UNIT_SIZE + ' m — a 4 × 3 layout covers 3.6 m × 2.7 m.';
      }
      return isM
        ? '= ' + c.tw + ' × ' + c.tl + ' tiles. Rough is fine — we round up to whole tiles.'
        : '= ' + c.covW.toFixed(1) + ' m × ' + c.covL.toFixed(1) + ' m finished size.';
    }

    function coverageLine(c) {
      var kg = ' · ~' + Math.round(c.weight) + ' kg';
      if (state.mode !== 'calc') return c.items + ' items' + kg;
      return 'Covers ' + c.covW.toFixed(1) + ' × ' + c.covL.toFixed(1) +
        ' m (' + c.covArea.toFixed(1) + ' m²)' + kg;
    }

    function deliveryLine(c) {
      return c.weight >= PALLET_KG ? DATA.palletNote : DATA.deliveryNote;
    }

    function summaryLine(c) {
      if (!c.rows.length) return '';
      if (state.mode !== 'calc') return c.items + ' items';
      var anyEdge = SIDES.some(function (k) { return state.edges[k]; });
      return c.tw + ' × ' + c.tl + ' ' +
        (state.type === 'solid' ? 'solid' : 'drainage') + ' tiles · ' +
        (anyEdge ? state.colour + ' edging' : 'no edging');
    }

    /* ================================================================ *
     *  Controls — handover §8
     * ================================================================ */
    function stepHead(no, title, echo) {
      return el('div', { class: 'fm-kit__step-head' }, [
        el('span', { class: 'fm-kit__step-no', text: String(no) }),
        el('span', { class: 'fm-kit__step-title', text: title }),
        echo ? el('span', { class: 'fm-kit__step-echo', text: echo }) : null
      ]);
    }

    function pill(label, pressed, onClick, dotColour) {
      return el('button', {
        type: 'button',
        class: 'fm-kit__pill' + (pressed ? ' is-selected' : ''),
        'aria-pressed': String(pressed),
        on: { click: onClick }
      }, [
        dotColour ? el('span', { class: 'fm-kit__pill-dot', style: 'background:' + dotColour }) : null,
        label
      ]);
    }

    /* Returns null when there is nothing to choose — one tile type is not a
       decision, and a step containing a single pre-selected pill is furniture
       the shopper has to read past. The remaining steps renumber themselves. */
    function stepTileType(c, stepNo) {
      if (TILE_TYPES.length < 2) return null;
      return el('div', { class: 'fm-kit__step' }, [
        stepHead(stepNo, 'Select tile type'),
        el('div', { class: 'fm-kit__pills' }, TILE_TYPES.map(function (t) {
          return pill(t.label, state.type === t.id, function () { setStep({ type: t.id }); });
        })),
        el('div', {
          class: 'fm-kit__hint',
          text: state.type === 'solid'
            ? 'Best for general traffic — workshops, assembly and packing areas.'
            : 'For wet areas — wash bays, poolside and outdoors. Water drains straight through.'
        })
      ]);
    }

    /* A text field accepts anything, so it is filtered here rather than left
       to `parseFloat` to shrug at. A comma is treated as a decimal point —
       someone typing 3,6 means three point six, and silently getting 3 would
       under-order by a whole row of tiles. Tiles mode takes whole numbers
       only; there is no such thing as 4.5 tiles. */
    function cleanNumeric(value, allowDecimal) {
      var s = String(value).replace(',', '.').replace(/[^0-9.]/g, '');
      if (!allowDecimal) return s.replace(/\./g, '');
      var i = s.indexOf('.');
      if (i === -1) return s;
      /* Keep the first dot, drop any later ones. */
      return s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '');
    }

    function dimensionInput(which, c) {
      var isM = state.unit === 'm';
      return el('span', { class: 'fm-kit__field' }, [
        el('input', {
          /* `type="text"` with `inputmode`, NOT `type="number"`.
             A number input reports `value === ''` for any intermediate state
             it considers invalid — and "3." is one of them. So typing 3 . 6
             blanked the state on the middle keystroke, the component
             re-rendered with an empty field, and the 6 landed in an empty box:
             decimals were impossible to enter and 3.6 m could not be asked
             for at all. Keeping the raw string means "3." survives long
             enough to become "3.6"; parseFloat reads it fine either way.
             `inputmode` still gets the numeric keypad on a phone, and text
             inputs support setSelectionRange, so the caret restore after a
             re-render works properly now too. */
          type: 'text',
          inputmode: isM ? 'decimal' : 'numeric',
          autocomplete: 'off',
          value: which === 'w' ? state.w : state.l,
          placeholder: which === 'w' ? 'Width' : 'Length',
          'aria-label': which === 'w' ? 'Area width' : 'Area length',
          'data-fm-kit-focus': which,
          on: {
            input: function (ev) {
              var raw = cleanNumeric(ev.target.value, isM);
              /* Write the cleaned string back before re-rendering, so the
                 caret offset captured next still refers to this text. */
              if (raw !== ev.target.value) ev.target.value = raw;
              var patch = {};
              patch[which] = raw;
              setStep(patch);
            }
          }
        }),
        el('span', { class: 'fm-kit__field-unit', text: isM ? 'm' : 'tiles' })
      ]);
    }

    function stepArea(c, stepNo) {
      var isM = state.unit === 'm';
      return el('div', { class: 'fm-kit__step' }, [
        el('div', { class: 'fm-kit__step-head' }, [
          el('span', { class: 'fm-kit__step-no', text: String(stepNo) }),
          el('span', { class: 'fm-kit__step-title', text: 'Enter your area' }),
          el('span', { class: 'fm-kit__toggle' }, [
            el('button', {
              type: 'button',
              class: 'fm-kit__toggle-btn' + (isM ? ' is-selected' : ''),
              'aria-pressed': String(isM),
              text: 'Metres',
              on: { click: function () { setUnit('m'); } }
            }),
            el('button', {
              type: 'button',
              class: 'fm-kit__toggle-btn' + (!isM ? ' is-selected' : ''),
              'aria-pressed': String(!isM),
              text: 'Tiles',
              on: { click: function () { setUnit('tiles'); } }
            })
          ])
        ]),
        el('div', { class: 'fm-kit__dims' }, [
          dimensionInput('w', c),
          el('span', { class: 'fm-kit__dims-x', text: '×', 'aria-hidden': 'true' }),
          dimensionInput('l', c)
        ]),
        el('div', { class: 'fm-kit__hint', text: areaHint(c) })
      ]);
    }

    /* Switching units converts what is already entered rather than clearing
       it — a shopper who typed 3.6 × 2.7 and then wondered "how many tiles is
       that?" should get 4 × 3, not an empty form. */
    function setUnit(u) {
      if (u === state.unit) return;
      var c = calc();
      if (u === 'tiles') {
        setStep({ unit: 'tiles', w: c.tw ? String(c.tw) : '', l: c.tl ? String(c.tl) : '' });
      } else {
        setStep({
          unit: 'm',
          w: c.tw ? String(round2(c.tw * UNIT_SIZE)) : '',
          l: c.tl ? String(round2(c.tl * UNIT_SIZE)) : ''
        });
      }
    }

    function allEdges(on) {
      return { top: on, right: on, bottom: on, left: on };
    }

    var COLOUR_SWATCH = { yellow: '#f2c230', black: '#26282e' };
    var COLOUR_LABEL = { yellow: 'Yellow', black: 'Black' };

    function stepEdging(c, stepNo) {
      if (!EDGE_COLOURS.length) return null;   /* no strips stocked at all */
      var anyEdge = SIDES.some(function (k) { return state.edges[k]; });

      var pills = EDGE_COLOURS.map(function (col) {
        /* Picking a colour also re-enables all four sides. Choosing yellow
           after switching everything off otherwise looks like a dead button —
           the colour changes but nothing is edged. */
        return pill(COLOUR_LABEL[col], anyEdge && state.colour === col, function () {
          setStep({ colour: col, edges: allEdges(true) });
        }, COLOUR_SWATCH[col]);
      });
      pills.push(pill('No edging', !anyEdge, function () {
        setStep({ edges: allEdges(false) });
      }));

      return el('div', { class: 'fm-kit__step' }, [
        stepHead(stepNo, 'Choose edging', edgeEcho(c)),
        el('div', { class: 'fm-kit__pills' }, pills),
        el('div', {
          class: 'fm-kit__hint',
          /* The yellow sell only makes sense where yellow is stocked. */
          text: EDGE_COLOURS.indexOf('yellow') > -1
            ? 'Ramped edging stops trips at the mat edge. Yellow is the high-visibility safety choice.'
            : 'Ramped edging stops trips at the mat edge.'
        })
      ]);
    }

    /* ================================================================ *
     *  Manual / spares mode — handover §10
     * ================================================================ */
    function manualBlock(c) {
      /* Spares can only be ordered for parts this product stocks — the same
         rule as the calculator's steps, applied to the rows. */
      var rows = [
        { key: 'solid', label: 'Solid tile, 0.9 × 0.9 m', extra: '' },
        { key: 'drain', label: 'Drainage tile, 0.9 × 0.9 m', extra: '' },
        { key: 'edgeA', label: 'Male edge strip, ' + state.colour, extra: ' · tabs, corner attached' },
        { key: 'edgeB', label: 'Female edge strip, ' + state.colour, extra: ' · sockets, corner attached' }
      ].filter(function (r) { return resolved(manualPart(r.key)); });

      /* The colour swap is only a choice where a second colour is stocked. */
      var other = null;
      if (EDGE_COLOURS.length > 1) {
        other = EDGE_COLOURS[(EDGE_COLOURS.indexOf(state.colour) + 1) % EDGE_COLOURS.length];
      }

      var intro = ['For trade orders, re-orders and spares — order any mix, from a single edge strip up.'];
      if (other) {
        intro.push(' Edging colour: ');
        intro.push(el('a', {
          href: '#',
          class: 'fm-kit__link',
          text: state.colour + ' — switch to ' + other,
          on: {
            click: function (ev) {
              ev.preventDefault();
              state.colour = other;
              state.added = false;
              render();
            }
          }
        }));
      }

      return el('div', { class: 'fm-kit__step' }, [
        stepHead(1, 'Enter quantities'),
        el('div', { class: 'fm-kit__hint' }, intro),
        el('div', { class: 'fm-kit__manual' }, rows.map(function (r) {
          var part = manualPart(r.key);
          var qty = state.manual[r.key] || 0;
          var unitPrice = part && part.price !== null && part.price !== undefined ? part.price : null;
          return el('div', { class: 'fm-kit__manual-row' }, [
            el('span', { class: 'fm-kit__manual-meta' }, [
              el('span', { class: 'fm-kit__manual-label', text: r.label }),
              el('span', {
                class: 'fm-kit__manual-sub',
                text: unitPrice === null
                  ? 'Currently unavailable'
                  : dispMoney(unitPrice) + ' ' + vatSuffix() + ' each' + r.extra
              })
            ]),
            stepper(qty, r.label, unitPrice === null, function (d) {
              var m = {};
              Object.keys(state.manual).forEach(function (k) { m[k] = state.manual[k]; });
              m[r.key] = Math.max(0, (m[r.key] || 0) + d);
              state.manual = m;
              state.added = false;
              render();
            }, 'manual-' + r.key),
            el('span', {
              class: 'fm-kit__manual-line',
              text: qty && unitPrice !== null ? dispMoney(qty * unitPrice) : '—'
            })
          ]);
        }))
      ]);
    }

    function stepper(qty, label, disabled, onDelta, focusKey) {
      return el('span', { class: 'fm-kit__stepper' }, [
        el('button', {
          type: 'button',
          class: 'fm-kit__step-btn',
          'aria-label': 'Fewer: ' + label,
          disabled: disabled || qty <= 0,
          'data-fm-kit-focus': focusKey + '-dec',
          text: '−',
          on: { click: function () { onDelta(-1); } }
        }),
        el('span', { class: 'fm-kit__stepper-val', 'aria-live': 'polite', text: String(qty) }),
        el('button', {
          type: 'button',
          class: 'fm-kit__step-btn',
          'aria-label': 'More: ' + label,
          disabled: disabled,
          'data-fm-kit-focus': focusKey + '-inc',
          text: '+',
          on: { click: function () { onDelta(1); } }
        })
      ]);
    }

    /* ================================================================ *
     *  Kit list — handover §9
     * ================================================================ */
    function motif(kind) {
      if (kind === 'tile') {
        return svg('svg', { width: 16, height: 16, 'aria-hidden': 'true', class: 'fm-kit__motif' },
          svg('rect', { width: 16, height: 16, rx: 3, fill: '#262a2e' }));
      }
      var yellow = state.colour === 'yellow';
      var fill = yellow ? '#f2c230' : '#4a4f57';
      var mot = yellow ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.7)';
      var kids = [svg('rect', { y: 5, width: 16, height: 8, rx: 1, fill: fill, stroke: 'rgba(0,0,0,0.25)' })];
      if (kind === 'A') {
        kids.push(svg('rect', { x: 3, y: 2, width: 3.5, height: 3.5, fill: mot }));
        kids.push(svg('rect', { x: 10, y: 2, width: 3.5, height: 3.5, fill: mot }));
      } else {
        kids.push(svg('line', {
          x1: 2, y1: 9, x2: 14, y2: 9, stroke: mot, 'stroke-width': 1.5, 'stroke-dasharray': '3 2'
        }));
      }
      return svg('svg', {
        width: 16, height: 16, viewBox: '0 0 16 16', 'aria-hidden': 'true', class: 'fm-kit__motif'
      }, kids);
    }

    function setOverride(kind, delta) {
      var c = calc();
      var current = kind === 'tile' ? c.effT : kind === 'A' ? c.effA : c.effB;
      var ov = { tile: state.ov.tile, A: state.ov.A, B: state.ov.B };
      ov[kind] = Math.max(0, current + delta);
      state.ov = ov;
      state.added = false;
      render();
    }

    function kitList(c) {
      var kids = [];

      if (state.mode === 'calc' ? c.empty : !c.rows.length) {
        kids.push(el('div', {
          class: 'fm-kit__empty',
          text: 'Your kit list builds itself as you enter dimensions.'
        }));
      }

      c.rows.forEach(function (r) {
        kids.push(el('div', { class: 'fm-kit__row' }, [
          motif(r.kind),
          state.mode === 'calc'
            ? stepper(r.qty, r.label, !r.part, function (d) { setOverride(r.kind, d); }, 'row-' + r.kind)
            : el('span', { class: 'fm-kit__row-qty', text: r.qty + ' ×' }),
          el('span', { class: 'fm-kit__row-label' }, [
            r.label,
            r.sub ? el('span', { class: 'fm-kit__row-sub', text: ' ' + r.sub }) : null
          ]),
          /* LIST price per line. The discount is taken once, at the total —
             showing already-discounted lines here would leave the struck
             "Kit price" figure above the total reconciling with nothing the
             shopper can see. A bill of materials should add up. */
          el('span', {
            class: 'fm-kit__row-price',
            text: r.qty && r.listPrice !== null ? dispMoney(r.listPrice) : '—'
          })
        ]));
      });

      if (c.adjusted) {
        kids.push(el('div', { class: 'fm-kit__adjusted' }, [
          'Quantities adjusted from the suggested kit — ',
          el('a', {
            href: '#',
            class: 'fm-kit__link',
            text: 'reset to suggested',
            on: {
              click: function (ev) {
                ev.preventDefault();
                state.ov = clearOv();
                state.added = false;
                render();
              }
            }
          })
        ]));
      }

      if (c.rows.length) {
        kids.push(el('div', { class: 'fm-kit__meta' }, [
          el('span', { text: coverageLine(c) }),
          el('br'),
          el('span', { text: deliveryLine(c) })
        ]));
      }

      if (!c.priceable) {
        kids.push(el('div', {
          class: 'fm-kit__adjusted',
          text: 'One of these parts is not available online right now — call ' +
            DATA.phone + ' and we will price the kit for you.'
        }));
      }

      return el('div', { class: 'fm-kit__list' }, kids);
    }

    /* ================================================================ *
     *  Over-range lead capture — handover §11
     * ================================================================ */
    function overPanel(c) {
      var kids = [
        el('div', {
          class: 'fm-kit__over-title',
          text: 'Over ' + MAX_AREA + ' m²? You’ll beat the web price.'
        }),
        el('div', {
          class: 'fm-kit__over-body',
          text: 'Areas this size are priced as projects — volume rates, arranged pallet delivery, and help planning the layout. Leave a number and we’ll call you back within one working day.'
        })
      ];

      if (state.leadSent) {
        kids.push(el('div', {
          class: 'fm-kit__over-done',
          role: 'status',
          text: 'Thanks — we’ll be in touch within one working day.'
        }));
      } else if (DATA.leadUrl) {
        kids.push(el('div', { class: 'fm-kit__over-form' }, [
          el('input', {
            type: 'text',
            class: 'fm-kit__over-input',
            placeholder: 'Phone or email',
            'aria-label': 'Phone or email',
            value: state.leadVal,
            'data-fm-kit-focus': 'lead',
            on: { input: function (ev) { state.leadVal = ev.target.value; } }
          }),
          el('button', {
            type: 'button',
            class: 'fm-kit__over-cta',
            disabled: state.leadBusy,
            text: state.leadBusy ? 'Sending…' : 'Request a project price',
            on: { click: function () { submitLead(c); } }
          })
        ]));
      }

      kids.push(el('div', { class: 'fm-kit__over-tel' }, [
        DATA.leadUrl && !state.leadSent ? 'Or call us now on ' : 'Call us now on ',
        el('a', { href: 'tel:' + DATA.phone.replace(/\s+/g, ''), text: DATA.phone })
      ]));

      return el('div', { class: 'fm-kit__over' }, kids);
    }

    function submitLead(c) {
      var contact = (state.leadVal || '').trim();
      if (!contact || state.leadBusy) return;
      state.leadBusy = true;
      render();

      fetch(DATA.leadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'tile-kit-over-range',
          contact: contact,
          product_handle: DATA.handle,
          product_title: DATA.title,
          width_m: state.unit === 'm' ? c.wN : round2(c.tw * UNIT_SIZE),
          length_m: state.unit === 'm' ? c.lN : round2(c.tl * UNIT_SIZE),
          entered_area_m2: c.enteredArea,
          tiles: c.tiles,
          tile_type: state.type,
          page_url: window.location.href
        })
      }).then(function () {
        state.leadBusy = false;
        state.leadSent = true;
        render();
      }).catch(function () {
        /* Never swallow it silently — a lost project enquiry is the most
           expensive failure on this page. */
        state.leadBusy = false;
        state.error = 'Sorry — that did not send. Please call ' + DATA.phone + '.';
        render();
      });
    }

    /* ================================================================ *
     *  Assembly
     * ================================================================ */
    function ctaLabel(c, withPrice) {
      if (state.busy) return 'Adding to basket…';
      if (state.added) return '✓ Added';
      /* The CTA price stays EX VAT whatever the page's display mode is —
         the sitewide rule (headline flips, cart button does not). */
      if (withPrice && c.items > 0 && c.priceable && !c.over) {
        return 'Add to basket — ' + money(c.totalEx);
      }
      return 'Add to basket';
    }

    function canAdd(c) {
      return c.items > 0 && c.priceable && !c.over && !state.busy;
    }

    /* The saving, said once and in pounds.
       Three lines only when there IS a discount — list price struck through,
       the saving in green with the percentage that earned it, then the total
       they pay. No table of tiers and no "spend £X more" nudge: the kit has
       already earned the break by the time this renders, and the cart drawer
       does the upselling (the same call the options sheet made on 31-Jul). */
    function savingRows(c) {
      if (!c.saving || !c.priceable) return null;
      return el('div', { class: 'fm-kit__saving' }, [
        el('span', { class: 'fm-kit__saving-line' }, [
          el('span', { class: 'fm-kit__saving-label', text: 'Kit price' }),
          el('span', { class: 'fm-kit__saving-was', text: dispMoney(c.totalList) })
        ]),
        el('span', { class: 'fm-kit__saving-line fm-kit__saving-line--win' }, [
          el('span', { class: 'fm-kit__saving-label' }, [
            el('span', { class: 'fm-kit__saving-tick', 'aria-hidden': 'true', text: '✓' }),
            'Bulk discount (' + c.tierPct + '% off ' + c.bandQty + ' items)'
          ]),
          el('span', { class: 'fm-kit__saving-amount', text: '−' + dispMoney(c.saving) })
        ])
      ]);
    }

    function totalRow(c) {
      var showable = c.rows.length && c.items > 0 && c.priceable;
      var primary = showable ? dispMoney(c.totalEx) + ' ' + vatSuffix() : '—';
      var secondary = showable
        ? (vatInc() ? money(c.totalEx) + ' + VAT' : money(Math.round(c.totalEx * 1.2)) + ' inc VAT')
        : '';
      return el('div', { class: 'fm-kit__total-block' }, [
        savingRows(c),
        el('div', { class: 'fm-kit__total' }, [
          el('span', { class: 'fm-kit__total-label', text: 'Total' }),
          el('span', { class: 'fm-kit__total-value', text: primary }),
          secondary ? el('span', { class: 'fm-kit__total-alt', text: secondary }) : null
        ])
      ]);
    }

    function modeLink() {
      return el('div', { class: 'fm-kit__mode' }, el('a', {
        href: '#',
        class: 'fm-kit__link',
        text: state.mode === 'calc'
          ? 'Buying spares, extra edging, or know your quantities? Enter them directly'
          : '← Back to the kit calculator',
        on: {
          click: function (ev) {
            ev.preventDefault();
            state.mode = state.mode === 'calc' ? 'manual' : 'calc';
            state.added = false;
            state.error = '';
            render();
          }
        }
      }));
    }

    function planPanel(c, size) {
      var kids = [buildPlan(c, size)];

      if (state.mode === 'calc' && c.empty) {
        kids.push(el('div', { class: 'fm-kit-plan__overlay' }, [
          el('span', { class: 'fm-kit-plan__overlay-title', text: 'Enter your area to see your layout' }),
          el('button', {
            type: 'button',
            class: 'fm-kit-plan__suggest',
            text: 'Try 3 m × 2 m',
            on: { click: function () { setStep({ unit: 'm', w: '3', l: '2' }); } }
          })
        ]));
      } else if (state.mode !== 'calc') {
        kids.push(el('div', { class: 'fm-kit-plan__overlay fm-kit-plan__overlay--quiet' },
          el('span', {
            class: 'fm-kit-plan__overlay-note',
            text: 'The layout plan appears when you use the kit calculator — quantities entered here go straight in the basket.'
          })));
      }

      /* The wall hint lives INSIDE the panel, sitting on the plan itself.
         Below it, as a line of grey text among other lines of grey text, it
         was simply not read — and it is the only thing that tells anyone the
         diagram is interactive at all. On the plan it reads as a caption for
         the thing it describes, and it travels with the plan when the gallery
         swipes to a photo, which is exactly when it stops being relevant. */
      if (state.mode === 'calc' && !c.empty && !c.over &&
          SIDES.some(function (k) { return state.edges[k]; })) {
        kids.push(el('div', { class: 'fm-kit-plan__taphint' }, [
          el('span', { class: 'fm-kit-plan__taphint-icon', 'aria-hidden': 'true', text: '↕' }),
          size === 'mini'
            ? 'Tap a side to leave that edge off'
            : 'Against a wall? Tap any side of the plan to leave that edge off'
        ]));
      }

      kids.push(planKeyboardControls());
      kids.push(el('div', { class: 'fm-kit__sr', text: planTextEquivalent(c) }));

      return el('div', { class: 'fm-kit-plan fm-kit-plan--' + size }, kids);
    }

    /* One builder, three surfaces. `surface` only changes which pieces are
       present and which CSS modifier they carry — never the numbers. */
    /* One builder for the status, so every surface's copy is the same copy. */
    function statusBlock(status) {
      return el('div', { class: 'fm-kit__status' + (status.tone ? ' is-' + status.tone : '') },
        status.detail
          ? [el('strong', { class: 'fm-kit__status-head', text: status.text }),
             el('span', { class: 'fm-kit__status-detail', text: status.detail })]
          : status.text);
    }

    function renderControls(surface, c) {
      var frag = document.createDocumentFragment();
      var status = statusFor(c);
      var anyEdge = SIDES.some(function (k) { return state.edges[k]; });

      if (state.mode === 'calc') {
        /* Steps number themselves. A product that stocks one tile type has no
           "select tile type" step at all, and the area entry becomes step 1 —
           numbering a visible "2" above a missing "1" is worse than either. */
        var stepNo = 0;
        [stepTileType, stepArea, stepEdging].forEach(function (build) {
          var node = build(c, stepNo + 1);
          if (!node) return;
          stepNo++;
          frag.appendChild(node);
        });
      } else {
        frag.appendChild(manualBlock(c));
      }

      /* The sheet carries its own plan; on desktop the plan lives in the
         gallery slot, which is a different part of the DOM entirely. */
      if (surface === 'sheet') {
        var planWrap = el('div', { class: 'fm-kit__step' }, [planPanel(c, 'std')]);
        /* The size feedback reads under the plan, which is the picture of the
           thing it is describing. Desktop uses the copy under the gallery
           image for the same reason; only the page-level one under the price
           was ever in the wrong place (Jack, 07-Aug-2026). */
        if (status.text) planWrap.appendChild(statusBlock(status));
        /* No tap hint here — `planPanel` now renders it on the plan itself. */
        frag.appendChild(planWrap);
      }

      if (c.over) frag.appendChild(overPanel(c));

      if (!c.over) {
        frag.appendChild(kitList(c));

        /* Desktop carries the total and CTA inline; the sheet pins them. */
        if (surface === 'desktop') {
          frag.appendChild(totalRow(c));
          frag.appendChild(el('button', {
            type: 'button',
            class: 'fm-kit__cta' + (state.added ? ' is-added' : '') + (state.busy ? ' is-busy' : ''),
            disabled: !canAdd(c) && !state.added,
            text: ctaLabel(c, false),
            on: { click: function () { addToCart(c); } }
          }));
          if (state.error) {
            frag.appendChild(el('div', { class: 'fm-kit__error', role: 'status', text: state.error }));
          }
          frag.appendChild(modeLink());
        }
      }

      if (surface === 'sheet') {
        if (state.error) {
          frag.appendChild(el('div', { class: 'fm-kit__error', role: 'status', text: state.error }));
        }
        frag.appendChild(modeLink());
      }

      return frag;
    }

    /* ---------------------------------------------------------------- *
     *  Mounting
     * ---------------------------------------------------------------- */
    function renderPlanSlot(c) {
      /* The gallery slide. Rendered at `split` on desktop and `mini` on
         mobile — same node, so the gallery's slide indices never shift. */
      var slot = document.querySelector('[data-fm-kit-plan]');
      if (!slot) return;
      clear(slot);
      slot.appendChild(planPanel(c, isMobile() ? 'mini' : 'split'));
    }

    function renderStatusNodes(c) {
      var status = statusFor(c);
      var nodes = document.querySelectorAll('[data-fm-kit-status]');
      Array.prototype.forEach.call(nodes, function (n) {
        clear(n);
        if (status.text) {
          append(n, status.detail
            ? [el('strong', { class: 'fm-kit__status-head', text: status.text }),
               el('span', { class: 'fm-kit__status-detail', text: status.detail })]
            : status.text);
        }
        /* Collapse rather than hold a blank line. On mobile the inline copy
           sits between the price and the buy controls, where an empty
           `min-height: 1em` reads as a gap someone forgot to fill. Toggled in
           JS rather than with `:empty` — this theme has been bitten by
           `div:empty` before, and `hidden` cannot be defeated by a stray
           `display` rule further down the cascade here. */
        n.hidden = !status.text;
        n.classList.toggle('is-warn', status.tone === 'warn');
        n.classList.toggle('is-note', status.tone === 'note');
      });

      /* The tap hint used to be written into a `[data-fm-kit-taphint]` slot
         below the gallery. It is drawn on the plan itself now — see
         `planPanel` — so there is nothing to fill here. */
    }

    /* Compact form of the saving for the two pinned footers, where there is
       no room for the full three-line breakdown: the list price struck
       through, then what they pay. Same information, one line. */
    function priceWithSaving(c) {
      var now = dispMoney(c.totalEx) + ' ' + vatSuffix();
      if (!c.saving) return now;
      return [
        el('s', { class: 'fm-kit__was', text: dispMoney(c.totalList) }),
        ' ',
        now
      ];
    }

    function renderBar(c) {
      if (!bar) return;
      var hasKit = c.rows.length > 0 && c.items > 0;
      bar.setAttribute('data-mode', hasKit && !c.over ? 'selected' : 'slim');

      var summary = bar.querySelector('[data-fm-kit-bar-summary]');
      var total = bar.querySelector('[data-fm-kit-bar-total]');
      var cta = bar.querySelector('[data-fm-kit-bar-cta]');

      if (summary) {
        clear(summary);
        append(summary, [
          summaryLine(c) + ' · ',
          el('span', { class: 'fm-kit-buybar__change', text: 'Change' })
        ]);
      }
      if (total) {
        clear(total);
        append(total, c.priceable && c.items ? priceWithSaving(c) : '—');
      }
      if (cta) {
        cta.textContent = ctaLabel(c, false);
        cta.disabled = !canAdd(c) && !state.added;
        cta.classList.toggle('is-added', state.added);
        cta.classList.toggle('is-busy', state.busy);
      }
      measureBar();
    }

    function renderSheet(c) {
      if (!sheetBody) return;

      /* Hold the scroll position across the rebuild. The body is cleared and
         re-appended on every state change, which resets scrollTop to 0 — so
         nudging a quantity down in the kit list threw the shopper back up to
         step 1, on the one surface where that is the whole screen. Restored
         synchronously, before paint, so there is no visible jump. */
      var keepScroll = sheetBody.scrollTop;

      clear(sheetBody);
      /* Only build the sheet's copy of the form on mobile. Building both
         surfaces every render doubled the DOM for nothing and duplicated
         every `data-fm-kit-focus` key across the page. The breakpoint
         listener re-renders, so crossing it swaps surfaces cleanly. */
      if (isMobile()) sheetBody.appendChild(renderControls('sheet', c));
      /* Clamped, because the rebuilt content can be shorter than what it
         replaced (switching to spares mode drops three steps), and an
         out-of-range scrollTop silently becomes 0 — the very jump this is
         here to prevent. */
      sheetBody.scrollTop = Math.min(keepScroll, sheetBody.scrollHeight - sheetBody.clientHeight);

      if (!sheetFooter) return;
      sheetFooter.hidden = !!c.over;
      var items = sheetFooter.querySelector('[data-fm-kit-sheet-items]');
      var total = sheetFooter.querySelector('[data-fm-kit-sheet-total]');
      var cta = sheetFooter.querySelector('[data-fm-kit-sheet-cta]');
      if (items) {
        /* The saving replaces the item count here rather than crowding in
           beside it — "you saved £85.18" is the more useful of the two once
           it exists, and the count is on screen in the kit list above. */
        items.textContent = !c.items ? 'Your kit'
          : c.saving && c.priceable ? 'You save ' + dispMoney(c.saving)
          : 'Your kit · ' + c.items + ' items';
        items.classList.toggle('is-saving', !!(c.saving && c.priceable));
      }
      if (total) {
        clear(total);
        append(total, c.priceable && c.items ? priceWithSaving(c) : '—');
      }
      if (cta) {
        cta.textContent = state.added ? '✓ Added to basket' : ctaLabel(c, true);
        cta.disabled = !canAdd(c) && !state.added;
        cta.classList.toggle('is-added', state.added);
        cta.classList.toggle('is-busy', state.busy);
      }
    }

    /* A wholesale re-render is the only way to keep the surfaces honest, but
       it destroys the node the shopper is typing into. Capture what had focus
       and put it back — `data-fm-kit-focus` keys survive the rebuild because
       they are derived from state, not from node identity.

       Scoped to the surface the focus was in. The keys are only unique WITHIN
       a surface: restoring globally would hand focus to whichever copy sits
       first in the DOM, which on mobile is the hidden desktop mount — so
       every keystroke in the sheet would silently drop focus. Only one
       surface renders at a time now, but the scope is what makes that a
       design rather than a coincidence. */
    function surfaceOf(node) {
      while (node && node !== document.body) {
        if (node === controlsMount || node === sheetBody) return node;
        node = node.parentNode;
      }
      return null;
    }

    function captureFocus() {
      var a = document.activeElement;
      if (!a || !a.getAttribute) return null;
      var key = a.getAttribute('data-fm-kit-focus');
      if (!key) return null;
      var start = null;
      try { start = a.selectionStart; } catch (e) { /* type=number in some browsers */ }
      return { key: key, start: start, root: surfaceOf(a) };
    }

    function restoreFocus(snap) {
      if (!snap) return;
      var root = snap.root && snap.root.isConnected ? snap.root : document;
      var next = root.querySelector('[data-fm-kit-focus="' + snap.key + '"]');
      if (!next || next.disabled) return;
      next.focus({ preventScroll: true });
      if (snap.start !== null && snap.start !== undefined) {
        try { next.setSelectionRange(snap.start, snap.start); } catch (e) { /* not supported on type=number */ }
      }
    }

    /* `null`, not `false`, so the FIRST render counts as a transition and
       sets the opening slide.

       The plan is the LAST slide and its thumbnail is hidden until a kit
       exists (Jack, 10-Aug-2026). Landing on a diagram of a mat you have not
       described yet is confusing, and the earlier "plan first, then jump to
       the photo" arrangement made the main image visibly change under the
       shopper a moment after load. Last + hidden means the page simply opens
       on the product photo, with no jump, and "Your layout" appears at the
       end of the strip the moment an area is entered. */
    var lastHadKit = null;

    function render() {
      var c = calc();
      var snap = captureFocus();

      clear(controlsMount);
      if (!isMobile()) controlsMount.appendChild(renderControls('desktop', c));

      renderPlanSlot(c);
      renderStatusNodes(c);
      renderSheet(c);
      renderBar(c);

      /* Reveal the plan's thumbnail, and move to it, the first time a kit
         exists. Only on the transition — re-running every render would yank
         the gallery back while the shopper is looking at a photo.

         Nothing happens on the way IN to the empty state beyond hiding the
         thumb: the gallery is already on a photo, and calling slickGoTo on
         load would be the jump this arrangement exists to avoid. */
      var hasKit = state.mode === 'calc' && !c.empty;
      if (hasKit !== lastHadKit) {
        document.body.classList.toggle('fm-kit-has-plan', hasKit);
        if (hasKit) {
          showGallerySlide(planSlideIndex());
          scheduleFrameKit();
        } else {
          /* Cleared the size — allow the next completed kit to frame again. */
          framedOnce = false;
          clearTimeout(frameTimer);
        }
        lastHadKit = hasKit;
      }

      restoreFocus(snap);
    }

    /* ---------------------------------------------------------------- *
     *  Bring the parts and the CTA into view
     *  -------------------------------------
     *  On desktop the shopper types a size ~450px above the answer, so the
     *  bill of materials and Add to basket are off the bottom of the screen
     *  at the moment they become interesting (Jack, 10-Aug-2026).
     *
     *  Deliberately restrained:
     *   - Debounced. `hasKit` flips on the FIRST keystroke of the second
     *     dimension, so scrolling immediately would yank the page out from
     *     under someone still typing "2.65".
     *   - Once per kit. Re-framing on every later edit would fight anyone
     *     adjusting a quantity or tapping edges off.
     *   - Never if it is already on screen, and never against
     *     prefers-reduced-motion, where it jumps instead of animating.
     *   - Desktop only. Mobile has the builder in a sheet with a pinned CTA,
     *     so there is nothing out of reach to scroll to.
     * ---------------------------------------------------------------- */
    var frameTimer = null;
    var framedOnce = false;
    var FRAME_DELAY_MS = 850;

    function scheduleFrameKit() {
      if (framedOnce || isMobile()) return;
      clearTimeout(frameTimer);
      frameTimer = setTimeout(frameKit, FRAME_DELAY_MS);
    }

    function frameKit() {
      if (framedOnce || isMobile()) return;
      var list = controlsMount.querySelector('.fm-kit__list');
      var cta = controlsMount.querySelector('.fm-kit__cta');
      if (!list || !cta) return;

      var top = list.getBoundingClientRect().top;
      var bottom = cta.getBoundingClientRect().bottom;
      var vh = window.innerHeight;

      /* Already framed — leave the page where it is. */
      if (top >= 0 && bottom <= vh) { framedOnce = true; return; }

      var blockH = bottom - top;
      var offset = blockH + 32 <= vh
        ? top - (vh - blockH) / 2      /* fits: centre it */
        : top - 24;                    /* taller than the screen: pin its top */

      framedOnce = true;
      window.scrollBy({
        top: offset,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    /* Slick is initialised from `eq-product-script.js` on jQuery's ready,
       which can land after this deferred script's DOMContentLoaded — so a
       `slickGoTo` issued during the first render can arrive before there is a
       slider to receive it. Held and retried on load rather than dropped. */
    var pendingSlide = null;

    /* The plan is the last slide, so its index is one less than the slide
       count. Clones are excluded — slick duplicates slides for looping and
       they carry their own indices. */
    function planSlideIndex() {
      return Math.max(0, document.querySelectorAll(
        '.product-nav-slider .main-product__slider-media:not(.slick-cloned)'
      ).length - 1);
    }

    function showGallerySlide(index) {
      var $ = window.jQuery;
      var nav = $ && $('.product-nav-slider');
      if (!nav || !nav.length || !nav.hasClass('slick-initialized')) {
        pendingSlide = index;
        return;
      }
      pendingSlide = null;
      try { nav.slick('slickGoTo', index); } catch (e) { /* slick mid-teardown */ }
    }

    window.addEventListener('load', function () {
      if (pendingSlide !== null) showGallerySlide(pendingSlide);
    });

    /* ================================================================ *
     *  Cart — handover §11
     * ================================================================ */
    function kitId() {
      return 'K' + Date.now().toString(36).toUpperCase() +
        Math.random().toString(36).slice(2, 6).toUpperCase();
    }

    function kitProperty(c) {
      return c.covW.toFixed(1) + ' × ' + c.covL.toFixed(1) + ' m (' +
        c.tw + ' × ' + c.tl + ' tiles)';
    }

    function edgingProperty() {
      var on = SIDES.filter(function (k) { return state.edges[k]; });
      if (!on.length) return 'none';
      return state.colour + ', ' + (on.length === 4 ? 'all four sides' : on.join(' + '));
    }

    function addToCart(c) {
      if (!canAdd(c)) return;

      var id = kitId();
      var items = [];
      c.rows.forEach(function (r) {
        if (!r.qty || !r.part || !r.part.variantId) return;
        /* Underscore-prefixed keys are hidden from the shopper but travel
           through to the order, which is what groups a kit in fulfilment. */
        var props = { _kit_id: id };
        if (state.mode === 'calc') {
          props.Kit = kitProperty(c);
          /* Edging only on the strip lines. On a tile line it reads as if
             the tile itself were edged. */
          if (r.kind !== 'tile') props.Edging = edgingProperty();
        }
        items.push({ id: r.part.variantId, quantity: r.qty, properties: props });
      });
      if (!items.length) return;

      state.busy = true;
      state.error = '';
      render();

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      }).then(function () {
        state.added = true;
        render();
        if (typeof window.addToCartAndOpenDrawer === 'function') {
          window.addToCartAndOpenDrawer();
          settle();
        } else {
          window.location.href = '/cart';
        }
      }).catch(function () {
        state.busy = false;
        state.error = 'Sorry — that could not be added. Please try again.';
        render();
      });
    }

    /* The drawer's `.active` class is the only reliable signal that the add
       landed and the shopper can see it. Until then both CTAs stay disabled
       and say "Adding to basket…" — an optimistic reset re-enables the button
       mid-flight, which is the spam-click window the options sheet hit. */
    function whenDrawerOpens(onOpen, onTimeout) {
      var waited = 0;
      var timer = setInterval(function () {
        if (document.querySelector('cart-drawer.active, #CartDrawer.active')) {
          clearInterval(timer);
          onOpen();
          return;
        }
        waited += 150;
        if (waited >= 10000) {
          clearInterval(timer);
          onTimeout();
        }
      }, 150);
    }

    /* The kit SURVIVES the add (Jack, 07-Aug-2026).
       The options sheet wipes its selection after an add so closing the drawer
       never lands on a primed CTA — right for a variant picker, where
       re-choosing is two taps. A kit is not: the shopper has typed dimensions
       and possibly switched sides off, and throwing that away left them back
       on an empty form with a greyed-out button, which reads as broken rather
       than as finished.

       So the configuration stays and the button stays usable. The confirmation
       does the work the reset used to: "✓ Added" holds for a moment, then the
       CTA returns to normal. Any change to steps 1–3 clears it earlier via
       `setStep`. A second press deliberately adds a second kit — which is a
       real thing shoppers want, and the drawer behind shows exactly what is
       already in there. */
    var addedTimer = null;
    var ADDED_HOLD_MS = 2500;

    function settle() {
      whenDrawerOpens(function () {
        state.busy = false;
        state.added = true;
        closeSheet();
        render();

        /* The kit just added is now part of the cart, so it counts towards the
           band for anything added next — a second kit on top of the first can
           land in a higher tier than either would alone. */
        if (TIERS.length) refreshCartQty();

        clearTimeout(addedTimer);
        addedTimer = setTimeout(function () {
          /* Only stand down if nothing else has happened since. */
          if (state.added && !state.busy) {
            state.added = false;
            render();
          }
        }, ADDED_HOLD_MS);
      }, function () {
        state.busy = false;
        state.added = false;
        state.error = 'Added, but the basket did not open — check your basket before re-adding.';
        render();
      });
    }

    /* ================================================================ *
     *  Sheet open / close
     * ================================================================ */
    var lastFocus = null;
    var savedScrollY = 0;

    function isOpen() {
      return !!sheet && sheet.getAttribute('data-state') === 'open';
    }

    function openSheet() {
      if (!sheet || !isMobile() || isOpen()) return;
      lastFocus = document.activeElement;
      savedScrollY = window.scrollY || window.pageYOffset || 0;

      sheet.removeAttribute('inert');
      sheet.setAttribute('aria-hidden', 'false');
      sheet.setAttribute('data-state', 'open');
      document.body.classList.add('fm-kit-sheet-open');
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      var closeBtn = sheet.querySelector('.fm-kit-sheet__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      document.addEventListener('keydown', onKeydown);
      syncPanelHeight();
    }

    function closeSheet() {
      if (!sheet || !isOpen()) return;
      sheet.setAttribute('data-state', 'closed');
      sheet.setAttribute('aria-hidden', 'true');
      sheet.setAttribute('inert', '');
      document.body.classList.remove('fm-kit-sheet-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
      document.removeEventListener('keydown', onKeydown);
      syncPanelHeight();
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    }

    /* Keep the panel inside the VISUAL viewport.
       `max-height: 93%` resolves against the layout viewport, which does not
       shrink when the software keyboard opens — so on a real handset, tapping
       Width slid the pinned footer (and with it the priced Add to basket)
       behind the keyboard. `dvh` does not help either: it tracks browser
       chrome, not the keyboard. visualViewport is the only thing that reports
       both, so the panel's ceiling is written from it while the sheet is
       open, and cleared when it closes. */
    function syncPanelHeight() {
      var panel = sheet && sheet.querySelector('.fm-kit-sheet__panel');
      if (!panel) return;
      if (!isOpen() || !window.visualViewport) {
        panel.style.maxHeight = '';
        return;
      }
      /* The panel is bottom-anchored to the layout viewport, so the keyboard's
         height has to come off the top as well as the bottom. */
      var vv = window.visualViewport;
      var hidden = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      panel.style.maxHeight = Math.round(vv.height * 0.93) + 'px';
      panel.style.transform = hidden ? 'translateY(-' + hidden + 'px)' : '';
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', syncPanelHeight);
      window.visualViewport.addEventListener('scroll', syncPanelHeight);
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        closeSheet();
        return;
      }
      if (e.key === 'Tab' || e.keyCode === 9) trapFocus(e);
    }

    function trapFocus(e) {
      var panel = sheet && sheet.querySelector('.fm-kit-sheet__panel');
      if (!panel) return;
      var focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
        ' select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.querySelectorAll('[data-fm-kit-open]').forEach(function (n) {
      n.addEventListener('click', openSheet);
    });
    if (sheet) {
      sheet.querySelectorAll('[data-fm-kit-close]').forEach(function (n) {
        n.addEventListener('click', closeSheet);
      });
    }
    if (bar) {
      var barCta = bar.querySelector('[data-fm-kit-bar-cta]');
      if (barCta) barCta.addEventListener('click', function () { addToCart(calc()); });
    }
    if (sheetFooter) {
      var sheetCta = sheetFooter.querySelector('[data-fm-kit-sheet-cta]');
      if (sheetCta) sheetCta.addEventListener('click', function () { addToCart(calc()); });
    }

    /* Leaving the mobile breakpoint with the sheet open (rotation, a tablet
       resize) hides the panel via CSS but would strand the body scroll lock. */
    window.addEventListener('resize', function () {
      if (!isMobile() && isOpen()) closeSheet();
    });

    /* The footer's height changes between its two modes, so the page's bottom
       padding is measured rather than guessed. */
    function measureBar() {
      if (!bar || !isMobile()) return;
      var h = bar.getBoundingClientRect().height;
      if (h > 0) document.documentElement.style.setProperty('--fm-kit-buybar-height', h + 'px');
    }
    if (window.ResizeObserver && bar) {
      try { new ResizeObserver(measureBar).observe(bar); } catch (e) { /* resize listener covers it */ }
    }
    window.addEventListener('resize', measureBar);

    /* The plan re-renders at a different size profile across the breakpoint,
       so a rotation has to rebuild it rather than just rescale the SVG. */
    var wasMobile = isMobile();
    window.addEventListener('resize', function () {
      if (isMobile() !== wasMobile) {
        wasMobile = isMobile();
        /* The opening gallery slide differs by breakpoint — desktop opens on
           the plan, mobile on the photo — so crossing the boundary has to
           re-decide it, not just redraw. Without this a page first painted
           narrow stayed on the photo after widening to desktop, because
           `lastHadKit` had already settled and no transition fired. */
        lastHadKit = null;
        render();
      }
    });

    /* fm-vat.js owns the display mode for the whole page. The kit renders its
       prices from data rather than from [data-ex-price] attributes, so it
       re-renders on the event — one source of truth, no drift. */
    document.addEventListener('fm:vat-changed', render);

    /* ---- init ---- */

    /* Start on something this product actually stocks. The defaults assume the
       full range (solid tiles, yellow edging); on a listing that carries
       neither — the gym tiles are solid-only in black — the builder would
       otherwise open on an unresolvable selection and price everything as "—"
       before the shopper had touched anything. */
    if (TILE_TYPES.length && !TILE_TYPES.some(function (t) { return t.id === state.type; })) {
      state.type = TILE_TYPES[0].id;
    }
    if (EDGE_COLOURS.length && EDGE_COLOURS.indexOf(state.colour) === -1) {
      state.colour = EDGE_COLOURS[0];
    }
    /* No strips stocked at all — start with the edges off rather than
       quoting a kit whose edging cannot be supplied. */
    if (!EDGE_COLOURS.length) state.edges = allEdges(false);

    if (bar) bar.removeAttribute('hidden');
    document.body.classList.add('fm-kit-active');
    render();
    measureBar();
    /* Anything of this product already in the basket counts towards the
       discount band, so the quote is only exact once we know it. Fire and
       forget — `refreshCartQty` re-renders if the number moved. */
    if (TIERS.length) refreshCartQty();
  });
})();
